import { Table, Button } from "react-bootstrap";
import TablePaginationBar from "../components/TablePaginationBar";
import { config } from "../config/config";
import { getJourneys } from "../config/url";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import useFetchData from "../hooks/useFetchData";
import { FaSearch } from "react-icons/fa";

const JourneysManagement = () => {
  const user = useSelector((state) => state?.user);
  const [allJourneys, setAllJourneys] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  const navigate = useNavigate();
  const location = useLocation();

  const businessId = user.user?.businessId;
  const businessIdentifier = user.user?.businessIdentifier;
  
  const apiUrl = `${getJourneys}`;
  const journeysData = useFetchData(apiUrl, config);
  
  useEffect(() => {
    if (journeysData.data) {
      setLoading(false);
      
      // Make a copy and ensure each item has exercises
      const journeysWithExercises = [...journeysData.data].map((journey) => ({
        ...journey,
        // Ensure exercises is a number
        exercises: typeof journey.exercises === 'number' ? journey.exercises : 0
      }));
      
      setAllJourneys(journeysWithExercises);
    }
  }, [journeysData.data]);

  // Check if we're on the core page or tags page
  const isCorePage = location.pathname === '/journeys';

  const handleEditJourney = (journey) => {
    navigate("/future-self-questionnaire", {
      state: { journey },
    });
  };

  // Navigation handlers
  const handleCoreClick = () => {
    navigate('/journeys');
  };

  const handleTagsClick = () => {
    navigate('/tags');
  };

  const handleAddNew = () => {
    navigate(`/journeys/add`);
  };

  // Filter journeys based on search term, tag and status
  const filteredJourneys = allJourneys.filter(journey => {
    const matchesSearch = searchTerm === "" || 
      journey.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      journey.id.toString().includes(searchTerm);
    const matchesTag = selectedTag === "" || journey.tag === selectedTag;
    const matchesStatus = selectedStatus === "" || journey.status === selectedStatus;
    return matchesSearch && matchesTag && matchesStatus;
  });

  // Get unique tags for filter dropdown
  const uniqueTags = [...new Set(allJourneys.filter(j => j.tag).map(journey => journey.tag))];
  
  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentJourneys = filteredJourneys.slice(indexOfFirstItem, indexOfLastItem);
  
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // If we're not on the journeys page, don't render the journeys content
  if (!isCorePage) {
    return null; // The Tags component will be rendered by the router instead
  }

  return (
    <div className="app-page">
      <div className="pill-tabs">
        <button
          type="button"
          className={`pill-tab ${location.pathname === "/journeys" ? "active" : ""}`}
          onClick={handleCoreClick}
        >
          Core
        </button>
        <button
          type="button"
          className={`pill-tab ${location.pathname === "/tags" ? "active" : ""}`}
          onClick={handleTagsClick}
        >
          Tags
        </button>
      </div>

      <div className="bg-white rounded shadow-sm" style={{ overflow: "hidden" }}>
      <div className="app-page-toolbar">
        <h2 className="app-page-title">Journeys</h2>

        <div className="app-page-toolbar-filters">
          <div className="app-filter-search-wrap">
            <FaSearch className="app-filter-search-icon" />
            <input
              type="text"
              className="form-control app-filter-search"
              placeholder="Search by ID or Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: "240px" }}
            />
          </div>

          <select
            className="form-select app-filter-select"
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
            style={{ width: "140px" }}
          >
            <option value="">Tag</option>
            {uniqueTags.map((tag) => (
              <option key={tag} value={tag}>
                {tag}
              </option>
            ))}
          </select>

          <select
            className="form-select app-filter-select"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{ width: "140px" }}
          >
            <option value="">Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>

          <button
            className="app-filter-action-btn"
            onClick={handleAddNew}
          >
            Add New
          </button>
        </div>
      </div>
        
        
        <div>
          <Table
            className="mb-0"
            hover
            responsive
            style={{ borderCollapse: "collapse", borderSpacing: 0 }}
          >
            <thead
              style={{
                backgroundColor: "#064260",
                color: "white",
                height: "50px",
                textAlign: "center",
              }}
            >
              <tr>
                <th style={{ width: "15%" }}>ID</th>
                <th style={{ width: "25%" }}>Name</th>
                <th style={{ width: "15%" }}>Exercises</th>
                <th style={{ width: "15%" }}>Tag</th>
                <th style={{ width: "15%" }}>Status</th>
                <th style={{ width: "15%" }}>Action</th>
              </tr>
            </thead>
            
            {loading ? (
              <tbody>
                <tr>
                  <td colSpan="6" className="text-center py-4">Loading...</td>
                </tr>
              </tbody>
            ) : (
              <tbody
                style={{
                  color: "#575757",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                {currentJourneys && currentJourneys.length > 0 ? (
                  currentJourneys.map((journey) => (
                    <tr key={journey.id}>
                      <td>
                        {journey.id.toString().padStart(4, "0")}
                      </td>
                      <td>{journey.name}</td>
                      <td>{journey.exercises !== undefined ? journey.exercises : 0}</td>
                      <td>
                        {Array.isArray(journey.tag)
                          ? journey.tag.join(', ')
                          : typeof journey.tag === 'string'
                          ? journey.tag
                          : "-"}
                      </td>                      
                      <td>{journey.status || "-"}</td>
                      <td>
                        <button
                          className="app-view-btn"
                          onClick={() => handleEditJourney(journey)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">No journeys found</td>
                  </tr>
                )}
              </tbody>
            )}
          </Table>
          
        </div>

        {!loading && filteredJourneys && filteredJourneys.length > 0 && (
          <TablePaginationBar
            currentPage={currentPage}
            totalItems={filteredJourneys.length}
            rowsPerPage={itemsPerPage}
            onPageChange={paginate}
            onRowsPerPageChange={(n) => {
              setItemsPerPage(n);
              setCurrentPage(1);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
      </div>
    </div>
  );
};

export default JourneysManagement;